import {ShippingType} from '../../../declarations/OrderIntake.enums'
import {
  Contact,
  DeliveryTime,
  CapacityRequest,
  SlotSiloConfig,
  ContactInformation,
  HaulerInfo
} from '../../../declarations/types'

import {SelectedSplitLoadMaterials} from './types'

/**
 * Sales order line item for split load - represents one material in the order
 */
interface SalesOrderLineItem {
  materialNumber: string
  materialDescription?: string
  contractNumber: string
  contractItemPositionNumber: string
  deliveryDate: string
  deliveryTime: DeliveryTime
  capacity: CapacityRequest
  bomMaterialNumber?: string
  bomMaterialPositionNumber?: string
  slot?: SlotSiloConfig
}

interface V2SalesOrderLineItem {
  contractNumber: string
  contractItem: string
  materialNumber: string
  capacity: CapacityRequest
  scheduleStartDate: string
  scheduleEndDate: string
  slot?: SlotSiloConfig
}

/**
 * Order intake payload for split load orders
 * Similar to OrderIntakePayload but with salesOrderLineItems array
 */
export interface SplitLoadOrderIntakePayload {
  isSendingConfirmationEmailUnChecked: boolean
  siteNumber: string
  contact: Contact
  materialEnteredNumber: string // First material number for backward compatibility
  customerReference: string
  shippingType: ShippingType
  customerId: string
  businessLineCode: string
  plantName: string
  plantNumber: string
  salesOrderLineItems: SalesOrderLineItem[]
  additionalDriverInfo?: {
    driverInstructions?: string
  }
  haulerInfo?: {
    companyName?: string
    driverName?: string
    licensePlate?: string
    phoneNumber?: string
  }
}

export interface V2SplitLoadOrderIntakePayload {
  country: string
  customerId: string
  businessLine: string
  customerReference: string
  orgUnitId: string
  shippingType: ShippingType
  siteId: string
  contactInformation: ContactInformation
  haulerInfo?: HaulerInfo
  instructions?: string
  allocatorInstruction?: string
  lineItems: V2SalesOrderLineItem[]
}

interface SplitLoadPayloadParams {
  splitLoadMaterials: SelectedSplitLoadMaterials
  sharedOrderData: {
    isSendingConfirmationEmailUnChecked: boolean
    siteNumber: string
    contact: {
      mainPhone: string
      backupPhone?: string
      fullName: string
      siteContactName?: string
      carbonCopyEmail?: string
    }
    customerReference: string
    shippingType: ShippingType
    customerId: string
    businessLineCode: string
    deliveryDate: string
    deliveryTime: {
      earliest: string
      latest: string
    }
    slot?: {
      loadingStation: string
      siloNumber: string
      externalReference: string
    }
    additionalDriverInfo?: {
      driverInstructions?: string
    }
    haulerInfo?: {
      companyName?: string
      driverName?: string
      licensePlate?: string
      phoneNumber?: string
    }
  }
}

/**
 * Builds the order intake payload for split load orders.
 *
 * For split load, multiple materials are ordered on the same truck,
 * sharing common delivery details (date, time, slot, site, contact) but
 * each having their own material-specific details (material number, contract,
 * quantity, BOM, etc.).
 *
 * @param params - Contains split load materials and shared order data
 * @returns SplitLoadOrderIntakePayload with salesOrderLineItems array containing all materials
 */
export const buildSplitLoadPayload = (
  params: SplitLoadPayloadParams
): SplitLoadOrderIntakePayload => {
  const {splitLoadMaterials, sharedOrderData} = params

  // Build salesOrderLineItems array from selected materials
  const salesOrderLineItems: SalesOrderLineItem[] = splitLoadMaterials.materials.map((material) => {
    // Find matching BOM material once instead of twice
    const matchingBom = material.bomMaterials?.find((bom) => bom.quantity === material.palletSize)

    return {
      materialNumber: material.materialNumber,
      materialDescription: material.materialDescription,
      contractNumber: material.contractNumber,
      contractItemPositionNumber: material.contractItemPositionNumber,
      deliveryDate: sharedOrderData.deliveryDate,
      deliveryTime: {
        earliest: sharedOrderData.deliveryTime.earliest,
        latest: sharedOrderData.deliveryTime.latest
      },
      capacity: {
        quantity: material.capacity.quantity,
        quantityType: material.capacity.quantityType
      },
      bomMaterialNumber: matchingBom?.bomMaterialNumber,
      bomMaterialPositionNumber: matchingBom?.bomMaterialPositionNumber,
      ...(sharedOrderData.slot && {
        slot: {
          loadingStation: sharedOrderData.slot.loadingStation,
          siloNumber: sharedOrderData.slot.siloNumber,
          externalReference: sharedOrderData.slot.externalReference
        }
      })
    }
  })

  // Build the complete payload
  const payload: SplitLoadOrderIntakePayload = {
    isSendingConfirmationEmailUnChecked: sharedOrderData.isSendingConfirmationEmailUnChecked,
    siteNumber: sharedOrderData.siteNumber,
    contact: {
      mainPhone: sharedOrderData.contact.mainPhone,
      backupPhone: sharedOrderData.contact.backupPhone || '',
      fullName: sharedOrderData.contact.fullName,
      siteContactName: sharedOrderData.contact.siteContactName || '',
      ...(sharedOrderData.contact.carbonCopyEmail && {
        carbonCopyEmail: sharedOrderData.contact.carbonCopyEmail
      })
    },
    // Use the first material's number as the main materialEnteredNumber
    // This maintains backward compatibility with the existing API structure
    materialEnteredNumber: splitLoadMaterials.materials[0].materialNumber,
    customerReference: sharedOrderData.customerReference,
    shippingType: sharedOrderData.shippingType,
    customerId: sharedOrderData.customerId,
    businessLineCode: sharedOrderData.businessLineCode,
    plantName: splitLoadMaterials.plant.plantName,
    plantNumber: splitLoadMaterials.plant.plantNumber,
    salesOrderLineItems,
    ...(sharedOrderData.additionalDriverInfo && {
      additionalDriverInfo: sharedOrderData.additionalDriverInfo
    }),
    ...(sharedOrderData.haulerInfo && {
      haulerInfo: sharedOrderData.haulerInfo
    })
  }

  return payload
}

interface MapToV2Params {
  payload: SplitLoadOrderIntakePayload
  orgUnitId: string
  country: string
  allocatorInstructions?: string
  siteId: string
}

export const mapSplitLoadPayloadToV2 = (params: MapToV2Params): V2SplitLoadOrderIntakePayload => {
  const {payload, orgUnitId, country, allocatorInstructions, siteId} = params

  const lineItems: V2SalesOrderLineItem[] = payload.salesOrderLineItems.map((item) => ({
    contractNumber: item.contractNumber,
    contractItem: item.contractItemPositionNumber,
    materialNumber: item.materialNumber,
    capacity: item.capacity,
    scheduleStartDate: `${item.deliveryDate}T${item.deliveryTime.earliest}`,
    scheduleEndDate: `${item.deliveryDate}T${item.deliveryTime.latest}`,
    ...(item.slot && {
      slot: {
        loadingStation: item.slot.loadingStation,
        siloNumber: item.slot.siloNumber,
        externalReference: item.slot.externalReference
      }
    })
  }))
  // Extract organization unit from customerId if needed
  const siteIdFallback = (() => {
    if (siteId.length > 0 && siteId.split('.').length === 2) {
      return siteId
    }

    const orgUnit = payload.customerId.split('.')[0]
    return siteId.length > 0 ? `${orgUnit}.${siteId}` : `${orgUnit}.${payload.siteNumber}`
  })()

  return {
    country,
    businessLine: payload.businessLineCode,
    customerId: payload.customerId,
    customerReference: payload.customerReference,
    orgUnitId: orgUnitId.length > 0 ? orgUnitId : payload.plantNumber,
    shippingType: payload.shippingType,
    siteId: siteIdFallback,
    contactInformation: {
      orderPlacerName: payload.contact.fullName ?? '',
      orderPlacerNumber: payload.contact.mainPhone ?? '',
      carbonCopyEmail: payload.contact.carbonCopyEmail ?? '',
      siteContactName: payload.contact.siteContactName ?? '',
      siteContactNumber: payload.contact.backupPhone ?? ''
    },
    instructions: payload.additionalDriverInfo?.driverInstructions ?? '',
    allocatorInstruction: allocatorInstructions ?? '',
    lineItems,
    ...(payload.haulerInfo && {
      haulerInfo: {
        trailerLicensePlate: payload.haulerInfo.licensePlate ?? '',
        truckLicensePlate: payload.haulerInfo.licensePlate ?? '',
        driverName: payload.haulerInfo.driverName ?? '',
        driverPhoneNumber: payload.haulerInfo.phoneNumber ?? ''
      }
    })
  }
}
